import { useSelector } from 'react-redux';
import { selectBrandingSettings } from 'legacyfrontend/redux/selectors/branding-selectors';
import {
  selectFooter,
  selectFooterLegalLinks,
  selectFooterUtilLinks,
  selectHeader,
  selectIsFlightSearcherEnabled,
  selectIsHotelSearcherEnabled,
  selectIsPackageSearcherEnabled,
  selectPaisId,
  selectTemplateData,
  selectWhatsAppNumber,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { useSearcherLinks } from 'frontend/features/searchers';
import { mapSettingsToBrandingProps } from './mapSettingsToBrandingProps';
import type { BrandingProps, LegacyMainMenuItem } from './mapSettingsToBrandingProps';

/**
 * Branding props for the current tenant, or null when the agency has not
 * published personalization yet — callers fall back to the legacy header.
 *
 * MUN-2237: once `settings != null`, the real site swaps MainHeader +
 * BillboardImage for the same Header + Hero the intranet's Personalizar editor
 * previews, fed by real data and the `searchers` feature's real forms.
 */
export function useBrandingProps(): BrandingProps | null {
  const settings = useSelector(selectBrandingSettings);
  const legacyHeader = useSelector(selectHeader);
  const legacyFooter = useSelector(selectFooter);
  const legacyFooterLegalLinks = useSelector(selectFooterLegalLinks);
  const legacyFooterUtilLinks = useSelector(selectFooterUtilLinks);
  const templateData = useSelector(selectTemplateData);
  const isPackageSearcherEnabled = useSelector(selectIsPackageSearcherEnabled);
  const isFlightSearcherEnabled = useSelector(selectIsFlightSearcherEnabled);
  const isHotelSearcherEnabled = useSelector(selectIsHotelSearcherEnabled);
  const whatsapp = useSelector(selectWhatsAppNumber);
  const paisId = useSelector(selectPaisId);
  const linkSearchers = useSearcherLinks();

  if (typeof window !== 'undefined') {
    console.log('[branding] settings', settings);
  }

  return mapSettingsToBrandingProps(settings, {
    email: legacyHeader.emailContacto,
    footer: legacyFooter,
    footerOtherLinks: [...legacyFooterLegalLinks, ...legacyFooterUtilLinks],
    isFlightSearcherEnabled,
    isHotelSearcherEnabled,
    isPackageSearcherEnabled,
    linkSearchers,
    logoImageUrl: legacyHeader.logo,
    mainMenu: templateData.mainMenu as LegacyMainMenuItem[] | undefined,
    paisId,
    phone: legacyHeader.telefonoContacto,
    socialNetworks: templateData.socialNetworks,
    whatsapp,
  });
}

/**
 * True when this agency renders the branding hero.
 *
 * The hero is route-independent: on `/buscarxpaquetes`, `/buscarxvuelos` and
 * `/buscarxhoteles` it is the same hero, already carrying the searcher tabs and
 * the active search form. Results pages use this to skip the legacy
 * `SearchSelector`, which would otherwise render a second searcher below it.
 */
export function useHasBrandingHero(): boolean {
  return useBrandingProps() !== null;
}
